import React, { useCallback, useEffect, useState } from "react";
import MaterialIcon from "../../../components/MaterialIcon";
import { $crud } from "../../../helper/CrudFactory";
import {
  getKjuupPlusBooking,
  requestKjuupPlusCancel,
  withdrawKjuupPlusCancel,
} from "../../../services/PaymentService";
import { priceMajor, t } from "../../Subscription/SubscriptionPage";
import { Card, CardHeader, Chip, SecondaryButton, Skeleton } from "./CockpitPrimitives";
import "./CockpitKjuupPlusAbo.css";

const INVOICE_PREVIEW = 3;

const formatDate = (value, language) => {
  if (!value) return "—";
  const code = String(language || "de").split("-")[0].toLowerCase();
  const locale = code === "en" ? "en-GB" : code === "de" || code === "gsw" || code === "wae" ? "de-DE" : code;
  return new Date(value).toLocaleDateString(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

function tierLabel(strings, tier, seats) {
  if (!tier) return "—";
  const max = (seats.tiers.find((x) => x.key === tier) || {}).maxSeats;
  return tier === "large"
    ? t(strings, "kjuup_plus_abo_tier_large", { max }, "Kjuup+ Large (bis {max} Mitarbeitende)")
    : t(strings, "kjuup_plus_abo_tier_small", { max }, "Kjuup+ Small (bis {max} Mitarbeitende)");
}

function Detail({ label, children }) {
  return (
    <div className="kp-abo-detail">
      <span className="kp-abo-detail-label">{label}</span>
      <span className="kp-abo-detail-value">{children}</span>
    </div>
  );
}

/**
 * Kjuup+ booking on the company tab (sidebar › Kjuup+ opens it): package,
 * seats still available, and — for company admins — billing, invoices and
 * the cancellation request. Kjuup confirms cancellations; admins only ask.
 */
export default function CockpitKjuupPlusAbo({ strings, language, companyId, focus }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [note, setNote] = useState("");
  const [allInvoices, setAllInvoices] = useState(false);

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    getKjuupPlusBooking(companyId).then(
      (next) => {
        if (cancelled) return;
        setData(next);
        setLoading(false);
      },
      () => {
        if (!cancelled) setLoading(false);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  useEffect(() => load(), [load]);

  useEffect(() => {
    if (!focus) return;
    const timer = setTimeout(() => {
      document.getElementById("cockpit-kjuup-plus-abo")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 200);
    return () => clearTimeout(timer);
  }, [focus, loading]);

  const run = async (action, successKey, successFallback) => {
    setBusy(true);
    try {
      setData(await action());
      setCancelOpen(false);
      setNote("");
      $crud.notify({ type: "success", message: strings[successKey] || successFallback });
    } catch (e) {
      $crud.notify({ type: "error", message: e.message });
    } finally {
      setBusy(false);
    }
  };

  if (loading && !data) {
    return (
      <Card className="kp-abo" id="cockpit-kjuup-plus-abo">
        <Skeleton height={12} width={120} />
        <Skeleton height={20} width="45%" />
        <Skeleton height={8} />
      </Card>
    );
  }
  if (!data || !data.isKjuup) return null;

  const { seats, isCompanyAdmin } = data;
  const sub = data.billedViaStripe ? data.subscription : null;
  const request = sub && sub.cancelRequest;
  const percent = Math.min(100, Math.round((seats.used / Math.max(1, seats.included)) * 100));
  const date = (v) => formatDate(v, language);
  const per =
    sub && sub.interval === "yearly"
      ? strings.kjuup_plus_abo_per_year || "/ Jahr"
      : strings.kjuup_plus_abo_per_month || "/ Monat";

  let statusChip = <Chip tone="green">{strings.kjuup_plus_abo_status_active || "Aktiv"}</Chip>;
  if (sub && sub.cancelAtPeriodEnd) {
    statusChip = (
      <Chip tone="red">
        {t(strings, "kjuup_plus_abo_status_ends", { date: date(sub.currentPeriodEnd) }, "Endet am {date}")}
      </Chip>
    );
  } else if (request) {
    statusChip = <Chip tone="slate">{strings.kjuup_plus_abo_status_requested || "Kündigung angefragt"}</Chip>;
  } else if (sub && sub.status === "past_due") {
    statusChip = <Chip tone="red">{strings.kjuup_plus_abo_status_past_due || "Zahlung offen"}</Chip>;
  }

  let seatsHint;
  if (seats.overCapacity) {
    seatsHint = t(
      strings,
      "kjuup_plus_abo_seats_over",
      { max: seats.included },
      "Mehr als {max} Mitarbeitende – Kjuup meldet sich mit einem individuellen Angebot."
    );
  } else if (seats.nextTier) {
    seatsHint = t(
      strings,
      "kjuup_plus_abo_seats_next",
      { next: tierLabel(strings, seats.nextTier.key, seats) },
      "Kommen mehr Mitarbeitende dazu, wechselt das Paket automatisch zu {next} (anteilig berechnet)."
    );
  }

  const invoices = data.invoices || [];
  const shownInvoices = allInvoices ? invoices : invoices.slice(0, INVOICE_PREVIEW);

  return (
    <Card className={`kp-abo${focus ? " kp-abo--focus" : ""}`} id="cockpit-kjuup-plus-abo">
      <CardHeader
        eyebrow={strings.kjuup_plus_abo_eyebrow || "Kjuup+ · Abo"}
        title={sub ? tierLabel(strings, sub.tier, seats) : strings.kjuup_plus_abo_title || "Kjuup+ Paket"}
        action={statusChip}
      />

      <div className="kp-abo-seats">
        <div className="kp-abo-seats-row">
          <span className="kp-abo-seats-count">
            {t(
              strings,
              "kjuup_plus_abo_seats_used",
              { used: seats.used, total: seats.included },
              "{used} von {total} Plätzen belegt"
            )}
          </span>
          <span className="kp-abo-seats-free">
            {seats.available === 1
              ? strings.kjuup_plus_abo_seats_free_one || "Noch 1 Platz frei"
              : t(strings, "kjuup_plus_abo_seats_free", { count: seats.available }, "Noch {count} Plätze frei")}
          </span>
        </div>
        <div
          className={`kp-abo-bar${seats.overCapacity ? " kp-abo-bar--over" : ""}`}
          role="progressbar"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <span style={{ width: `${percent}%` }} />
        </div>
        {seatsHint ? <p className="kp-abo-hint">{seatsHint}</p> : null}
      </div>

      {sub ? (
        <div className="kp-abo-details">
          <Detail label={strings.kjuup_plus_abo_billing || "Zahlweise"}>
            {sub.interval === "yearly"
              ? strings.kjuup_plus_abo_yearly || "Jährlich"
              : strings.kjuup_plus_abo_monthly || "Monatlich"}
          </Detail>
          {isCompanyAdmin && sub.unitAmount != null ? (
            <Detail label={strings.kjuup_plus_abo_amount || "Betrag"}>
              {priceMajor({ amount: sub.unitAmount, currency: sub.currency })} {per}
            </Detail>
          ) : null}
          <Detail
            label={
              sub.cancelAtPeriodEnd
                ? strings.kjuup_plus_abo_ends || "Endet am"
                : strings.kjuup_plus_abo_renews || "Nächste Verlängerung"
            }
          >
            {date(sub.currentPeriodEnd)}
          </Detail>
          {isCompanyAdmin && data.upcomingInvoice ? (
            <Detail label={strings.kjuup_plus_abo_next_invoice || "Nächste Rechnung"}>
              {priceMajor({
                amount: data.upcomingInvoice.total != null ? data.upcomingInvoice.total : data.upcomingInvoice.amountDue,
                currency: data.upcomingInvoice.currency,
              })}{" "}
              · {date(data.upcomingInvoice.issueDate)}
            </Detail>
          ) : null}
          {isCompanyAdmin && sub.intro && sub.introEndsAt ? (
            <Detail label={strings.kjuup_plus_abo_intro || "Einführungspreis"}>
              {t(strings, "kjuup_plus_abo_intro_until", { date: date(sub.introEndsAt) }, "bis {date}")}
            </Detail>
          ) : null}
          {sub.pendingTier ? (
            <Detail label={strings.kjuup_plus_abo_pending_tier || "Ab nächster Verlängerung"}>
              {tierLabel(strings, sub.pendingTier, seats)}
            </Detail>
          ) : null}
        </div>
      ) : (
        <p className="kp-abo-hint">
          {strings.kjuup_plus_abo_manual ||
            "Kjuup+ ist für deine Firma über eine individuelle Vereinbarung aktiv."}
        </p>
      )}

      {sub && isCompanyAdmin && invoices.length ? (
        <div className="kp-abo-invoices">
          <div className="kp-abo-section-title">{strings.kjuup_plus_abo_invoices || "Rechnungen"}</div>
          <ul>
            {shownInvoices.map((inv) => (
              <li key={inv.id}>
                <span className="kp-abo-inv-main">
                  <span className="kp-abo-inv-number">{inv.number || inv.id}</span>
                  <span className="kp-abo-inv-meta">
                    {date(inv.created)} ·{" "}
                    {inv.status === "paid"
                      ? strings.kjuup_plus_abo_invoice_paid || "Bezahlt"
                      : inv.status === "open"
                        ? t(
                            strings,
                            "kjuup_plus_abo_invoice_open",
                            { date: date(inv.dueDate) },
                            "Offen · fällig {date}"
                          )
                        : inv.status}
                  </span>
                </span>
                <span className="kp-abo-inv-amount">
                  {priceMajor({
                    amount: inv.total != null ? inv.total : inv.amountPaid != null ? inv.amountPaid : inv.amountDue,
                    currency: inv.currency,
                  })}
                </span>
                {inv.hostedInvoiceUrl || inv.invoicePdf ? (
                  <a
                    className="kj-btn kj-btn--ghost kj-btn--neutral kj-btn--sm"
                    href={inv.hostedInvoiceUrl || inv.invoicePdf}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <MaterialIcon icon="receipt_long" size={16} color="currentColor" weight={400} />
                    <span>{strings.kjuup_plus_abo_invoice_view || "Ansehen"}</span>
                  </a>
                ) : null}
              </li>
            ))}
          </ul>
          {invoices.length > INVOICE_PREVIEW ? (
            <button type="button" className="kj-btn kj-btn--ghost kj-btn--sm" onClick={() => setAllInvoices((v) => !v)}>
              {allInvoices
                ? strings.kjuup_plus_abo_invoices_less || "Weniger anzeigen"
                : t(strings, "kjuup_plus_abo_invoices_all", { count: invoices.length }, "Alle {count} anzeigen")}
            </button>
          ) : null}
        </div>
      ) : null}

      {sub && !isCompanyAdmin ? (
        <p className="kp-abo-hint">
          {strings.kjuup_plus_abo_member_hint || "Abrechnung und Kündigung verwalten die Firmen-Admins."}
        </p>
      ) : null}

      {sub && isCompanyAdmin && !sub.cancelAtPeriodEnd ? (
        <div className="kp-abo-cancel">
          {request ? (
            <>
              <p className="kp-abo-hint">
                {t(
                  strings,
                  "kjuup_plus_abo_requested_hint",
                  { date: date(request.requestedAt), end: date(sub.currentPeriodEnd) },
                  "Kündigung am {date} angefragt. Kjuup bestätigt sie zum Ende der Laufzeit ({end})."
                )}
              </p>
              <SecondaryButton
                disabled={busy}
                onClick={() =>
                  run(
                    () => withdrawKjuupPlusCancel(companyId),
                    "kjuup_plus_abo_withdrawn",
                    "Kündigungsanfrage zurückgezogen"
                  )
                }
              >
                {strings.kjuup_plus_abo_withdraw || "Anfrage zurückziehen"}
              </SecondaryButton>
            </>
          ) : cancelOpen ? (
            <div className="kp-abo-cancel-form">
              <p className="kp-abo-hint">
                {t(
                  strings,
                  "kjuup_plus_abo_cancel_hint",
                  { end: date(sub.currentPeriodEnd) },
                  "Kjuup prüft deine Anfrage und bestätigt die Kündigung zum Ende der Laufzeit ({end}). Bis dahin bleibt Kjuup+ aktiv."
                )}
              </p>
              <textarea
                className="kp-abo-note"
                rows={3}
                maxLength={1000}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={strings.kjuup_plus_abo_cancel_note || "Grund (optional)"}
              />
              <div className="kp-abo-actions">
                <button
                  type="button"
                  className="kj-btn kj-btn--danger"
                  disabled={busy}
                  onClick={() =>
                    run(
                      () => requestKjuupPlusCancel(companyId, note.trim()),
                      "kjuup_plus_abo_requested",
                      "Kündigung angefragt"
                    )
                  }
                >
                  <span>{strings.kjuup_plus_abo_cancel_send || "Kündigung anfragen"}</span>
                </button>
                <SecondaryButton disabled={busy} onClick={() => setCancelOpen(false)}>
                  {strings.cancel || "Abbrechen"}
                </SecondaryButton>
              </div>
            </div>
          ) : (
            <button type="button" className="kj-btn kj-btn--ghost kj-btn--neutral kj-btn--sm" onClick={() => setCancelOpen(true)}>
              <span>{strings.kjuup_plus_abo_cancel || "Abo kündigen"}</span>
            </button>
          )}
        </div>
      ) : null}
    </Card>
  );
}
